import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Camera, CameraResultType, CameraSource, ImageOptions, Photo, } from "@capacitor/camera"; // Import Photo interface
import { ActionSheetController, IonicModule, ModalController, ToastController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import _ from 'lodash';

import { endpoints } from 'src/app/constant/endpoints';
import { ApiService } from 'src/app/provider/api.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-post-modal',
    templateUrl: './post-modal.page.html',
    styleUrls: ['./post-modal.page.scss'],
})
export class PostModalPage implements OnInit {
    @Input() customer_id: any;
    settings: any = {}; loading: boolean = false;
    content: any; attachment: any;
    attachment_img: any;
    postForm: FormGroup;
    updateImage: string[] = [];
    uploadImage: any[] = [];
    imageOptionSetting: ImageOptions = { // Use ImageOptions type for better type safety
        quality: 100,
        width: 720, // Use width instead of targetWidth for consistency
        saveToGallery: false,
        allowEditing: false,
        source: CameraSource.Camera,
        resultType: CameraResultType.Base64, // For camera, Base64 is usually easier
    };
    imageSettingGallery: ImageOptions = { // <--- Corrected
        quality: 100,
        allowEditing: false,
        source: CameraSource.Photos,
        resultType: CameraResultType.Uri,
    };

    constructor(
        private formBuilder: FormBuilder,
        public modalCtrl: ModalController,
        private actionSheetCtrl: ActionSheetController,
        private toastCtrl: ToastController,
        private translate: TranslateService,
        private apiService: ApiService,
    ) {
        this.postForm = this.formBuilder.group({
            customer_id: new FormControl(this.customer_id, Validators.compose([
                Validators.required,
            ])),
            content: new FormControl('', Validators.compose([
                Validators.required,
                Validators.minLength(1),
            ])),
            // attachment field for the form, will be updated dynamically
            attachment: new FormControl(null, Validators.compose([])),
        });
    }

    ngOnInit() {
        console.log(this.customer_id);
        // Ensure customer_id is set for the form after input is available
        if (this.customer_id) {
            this.postForm.get('customer_id')?.setValue(this.customer_id);
        }
    }

    async openCameraORGallery() {
        const actionSheet = await this.actionSheetCtrl.create({
            buttons: [{
                text: this.translate.instant('choose_from_gallery'),
                icon: 'image',
                handler: () => {
                    this.pickImage(); // No need to pass options, use component's property
                }
            }, {
                text: this.translate.instant('capture_image'),
                icon: 'camera',
                handler: () => {
                    this.selectImage(); // No need to pass options
                }
            }]
        });
        await actionSheet.present();
    }

    async selectImage() { // For CameraSource.Camera
        try {
            const imageData = await Camera.getPhoto(this.imageOptionSetting);
            if (imageData.base64String) {
                this.attachment = `data:image/jpeg;base64,${imageData.base64String}`;
                this.attachment_img = `data:image/jpeg;base64,${imageData.base64String}`;
                this.postForm.get('attachment')?.setValue(imageData.base64String); // Update form control
            } else {
                console.warn('Camera returned no base64 string.');
            }
        } catch (err) {
            console.error('Error selecting image from camera:', err);
            // Optionally show a toast to the user
            this.toastMessage(this.translate.instant('image_capture_failed'));
        }
    }

    async pickImage() { // For CameraSource.Photos (Gallery)
        try {
            // Camera.pickImages returns an object with a 'photos' property which is an array of Photo
            const { photos } = await Camera.pickImages(this.imageSettingGallery);
            if (photos && photos.length > 0) {
                const photo = photos[0]; // Assuming you only want the first selected image
                if (photo.webPath) {
                    const response = await fetch(photo.webPath);
                    const blob = await response.blob();
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        this.attachment = reader.result as string;
                        this.attachment_img = reader.result as string;
                        // Extract base64 part only if needed for API (remove "data:image/jpeg;base64," prefix)
                        // const base64Only = (reader.result as string).split(',')[1];
                        this.postForm.get('attachment')?.setValue(reader.result);
                    };
                } else {
                    console.warn('Gallery returned no webPath for the photo.');
                }
            }
        } catch (err) {
            console.error('Error picking image from gallery:', err);
            // Optionally show a toast to the user
            this.toastMessage(this.translate.instant('image_selection_failed'));
        }
    }

    deleteImage = () => {
        this.attachment = null;
        this.attachment_img = null;
        this.postForm.get('attachment')?.setValue(null); // Clear form control
    };

    onSubmit = () => {
        if (this.postForm.invalid) {
            this.toastMessage(this.translate.instant('please_fill_all_required_fields'));
            console.warn('Form is invalid:', this.postForm.errors);
            return;
        }

        console.log(this.postForm.value);
        this.loading = true;
        this.apiService
            .post(endpoints.posts, this.postForm.value)
            .pipe(first())
            .subscribe(async (response) => {
                this.loading = false;
                console.log(response);
                if (response.success) {
                    this.modalCtrl.dismiss(true, 'success'); // Use 'success' role for clarity
                } else {
                    this.modalCtrl.dismiss(false, 'error'); // Use 'error' role
                }
                this.toastMessage(response.message);
            }, err => {
                this.loading = false; // Ensure loading is turned off even on API error
                console.error('API Error:', err);
                this.modalCtrl.dismiss(false, 'error');
                this.toastMessage(this.translate.instant('api_error_occurred')); // Generic error message
            });
    }

    closeModal = () => {
        this.modalCtrl.dismiss(false, 'cancel');
    }

    async toastMessage(msg: string) { // Type msg as string
        await this.toastCtrl.create({
            message: msg, // Use the passed message directly
            duration: 4000,
            position: 'top',
            color: 'primary'
        }).then((toastElem) => toastElem.present());
    }
}